import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  ChildCare as ChildIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { collection, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { auth, db } from '../../firebase';

const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  background: '#F8FDF8',
  text: '#2C3E50'
};

const ChildrenDataForm = ({ initialChildren = [], onComplete }) => {
  // Función para convertir string de fecha a objeto Date para el DatePicker
  const stringToDate = (dateString) => {
    if (!dateString) return null;

    try {
      // Si ya es un objeto Date, devolverlo
      if (dateString instanceof Date) return dateString;

      // Si es string, convertir a Date
      if (typeof dateString === 'string') {
        // Intentar formato DD/MM/YYYY
        const ddmmyyyy = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddmmyyyy) {
          const [, day, month, year] = ddmmyyyy;
          return new Date(year, month - 1, day);
        }

        // Intentar otros formatos
        const dateObj = new Date(dateString);
        return isNaN(dateObj.getTime()) ? null : dateObj;
      }

      return null;
    } catch (error) {
      console.error('Error converting string to date:', error);
      return null;
    }
  };

  // Función para convertir objeto Date a string para la base de datos
  const dateToString = (date) => {
    if (!date) return null;

    try {
      if (date instanceof Date && !isNaN(date.getTime())) {
        // Formatear como DD/MM/YYYY
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
      return null;
    } catch (error) {
      console.error('Error converting date to string:', error);
      return null;
    }
  };

  // Preparar children iniciales convirtiendo fechas string a Date para el formulario
  const prepareInitialChildren = (children) => {
    return children.map(child => ({
      ...child,
      fechaNacimiento: stringToDate(child.fechaNacimiento)
    }));
  };

  const [children, setChildren] = useState(
    initialChildren.length > 0
      ? prepareInitialChildren(initialChildren)
      : [createEmptyChild()]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function createEmptyChild() {
    return {
      id: null,
      nombre: '',
      apellidos: '',
      fechaNacimiento: null, // null para DatePicker (se convertirá en string al guardar)
      curso: '',
      clase: '',
      alergias: '',
      condicionesMedicas: '',
      necesidadesEspeciales: '',
      medicacionEmergencia: '',
      notas: ''
    };
  }

  const handleChildChange = (index, field, value) => {
    const updatedChildren = [...children];
    updatedChildren[index] = {
      ...updatedChildren[index],
      [field]: value
    };
    setChildren(updatedChildren);
    setError('');
  };

  const addChild = () => {
    setChildren([...children, createEmptyChild()]);
  };

  const removeChild = (index) => {
    if (children.length > 1) {
      const updatedChildren = children.filter((_, i) => i !== index);
      setChildren(updatedChildren);
    }
  };

  const validateChildren = () => {
    for (let i = 0; i < children.length; i++) {
      const child = children[i];

      if (!child.nombre?.trim()) {
        setError(`El nombre del hijo ${i + 1} es obligatorio`);
        return false;
      }

      if (!child.apellidos?.trim()) {
        setError(`Los apellidos del hijo ${i + 1} son obligatorios`);
        return false;
      }

      if (!child.fechaNacimiento) {
        setError(`La fecha de nacimiento del hijo ${i + 1} es obligatoria`);
        return false;
      }

      if (!child.curso) {
        setError(`El curso del hijo ${i + 1} es obligatorio`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateChildren()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const user = auth.currentUser;
      const savedChildren = [];

      for (const child of children) {
        const childData = {
          ...child,
          // Convertir fecha de Date a string para la base de datos
          fechaNacimiento: dateToString(child.fechaNacimiento),
          parentId: user.uid,
          updatedAt: new Date()
        };

        if (child.id) {
          // Actualizar hijo existente
          const childRef = doc(db, 'children', child.id);
          await updateDoc(childRef, childData);
          savedChildren.push({
            ...childData,
            id: child.id,
            // Mantener la fecha como string para el callback
            fechaNacimiento: childData.fechaNacimiento
          });
        } else {
          // Crear nuevo hijo
          childData.createdAt = new Date();
          const docRef = await addDoc(collection(db, 'children'), childData);
          savedChildren.push({
            ...childData,
            id: docRef.id,
            // Mantener la fecha como string para el callback
            fechaNacimiento: childData.fechaNacimiento
          });
        }
      }

      onComplete(savedChildren);

    } catch (error) {
      console.error('Error saving children data:', error);
      setError('Error al guardar los datos de los hijos. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const gradeOptions = [
    { value: 'infantil-3', label: 'Infantil 3 años' },
    { value: 'infantil-4', label: 'Infantil 4 años' },
    { value: 'infantil-5', label: 'Infantil 5 años' },
    { value: '1-primaria', label: '1º Primaria' },
    { value: '2-primaria', label: '2º Primaria' },
    { value: '3-primaria', label: '3º Primaria' },
    { value: '4-primaria', label: '4º Primaria' },
    { value: '5-primaria', label: '5º Primaria' },
    { value: '6-primaria', label: '6º Primaria' },
    { value: '1-eso', label: '1º ESO' },
    { value: '2-eso', label: '2º ESO' },
    { value: '3-eso', label: '3º ESO' },
    { value: '4-eso', label: '4º ESO' }
  ];

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Paper elevation={0} sx={{ p: 3, backgroundColor: COLORS.background }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ChildIcon sx={{ color: COLORS.accent, mr: 1, fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text }}>
              Datos de los Hijos
            </Typography>
          </Box>
          <Button
            onClick={addChild}
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            sx={{
              borderColor: COLORS.accent,
              color: COLORS.accent,
              '&:hover': {
                borderColor: COLORS.secondary,
                backgroundColor: COLORS.primary + '20'
              }
            }}
          >
            Añadir Hijo
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {children.map((child, index) => (
            <Grid item xs={12} key={index}>
              <Card sx={{ border: `1px solid ${COLORS.primary}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: COLORS.text }}>
                      Hijo {index + 1}
                    </Typography>
                    {children.length > 1 && (
                      <IconButton
                        onClick={() => removeChild(index)}
                        size="small"
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    {/* Datos básicos */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Nombre"
                        value={child.nombre}
                        onChange={(e) => handleChildChange(index, 'nombre', e.target.value)}
                        disabled={loading}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Apellidos"
                        value={child.apellidos}
                        onChange={(e) => handleChildChange(index, 'apellidos', e.target.value)}
                        disabled={loading}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Fecha de Nacimiento *"
                        value={child.fechaNacimiento}
                        onChange={(date) => handleChildChange(index, 'fechaNacimiento', date)}
                        disabled={loading}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Curso</InputLabel>
                        <Select
                          value={child.curso}
                          onChange={(e) => handleChildChange(index, 'curso', e.target.value)}
                          disabled={loading}
                          label="Curso"
                        >
                          {gradeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Aula/Clase"
                        value={child.clase}
                        onChange={(e) => handleChildChange(index, 'clase', e.target.value)}
                        disabled={loading}
                        placeholder="Ej: 3ºA, Infantil B..."
                      />
                    </Grid>

                    {/* Información médica */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Información Médica (Opcional)
                        </Typography>
                      </Divider>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Alergias"
                        value={child.alergias}
                        onChange={(e) => handleChildChange(index, 'alergias', e.target.value)}
                        disabled={loading}
                        multiline
                        rows={2}
                        placeholder="Alergias alimentarias, medicamentos..."
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Condiciones Médicas"
                        value={child.condicionesMedicas}
                        onChange={(e) => handleChildChange(index, 'condicionesMedicas', e.target.value)}
                        disabled={loading}
                        multiline
                        rows={2}
                        placeholder="Asma, diabetes, etc..."
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Necesidades Especiales"
                        value={child.necesidadesEspeciales}
                        onChange={(e) => handleChildChange(index, 'necesidadesEspeciales', e.target.value)}
                        disabled={loading}
                        multiline
                        rows={2}
                        placeholder="Necesidades educativas, físicas..."
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Medicación de Emergencia"
                        value={child.medicacionEmergencia}
                        onChange={(e) => handleChildChange(index, 'medicacionEmergencia', e.target.value)}
                        disabled={loading}
                        multiline
                        rows={2}
                        placeholder="Inhalador, epipen..."
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Notas Adicionales"
                        value={child.notas}
                        onChange={(e) => handleChildChange(index, 'notas', e.target.value)}
                        disabled={loading}
                        multiline
                        rows={2}
                        placeholder="Cualquier información adicional relevante..."
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              bgcolor: COLORS.accent,
              '&:hover': { bgcolor: COLORS.secondary },
              px: 4,
              py: 1.5
            }}
          >
            {loading ? 'Guardando...' : 'Guardar y Finalizar'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChildrenDataForm;